import type { FilterStatus } from "@/types/common/filters";
import { cn } from "@/utils/theme";
import { Badge, Checkbox, Icon } from "@elaniintc/goes-design-system";
import { useState } from "react";

interface FilterStateProps {
  filterLabel: string;
  groups: {
    label: string;
    status: {
      label: string;
      value: boolean;
      field: string;
      children: FilterStatus[];
    }[];
  }[];
  onStatesChange: (value: string, parentLabel?: string) => void;
}

export default function FilterHierarchyStatus({
  groups,
  filterLabel,
  onStatesChange,
}: FilterStateProps) {
  const shouldHide = groups.length > 1;
  return (
    <div className="p-6 max-h-full overflow-auto">
      <p className="text-h5 text-heading font-semibold">{filterLabel}</p>
      {groups.map((item, idx) => (
        <Wrapper
          key={`${item.label}-${idx}`}
          item={item}
          onStatesChange={onStatesChange}
          shouldHide={shouldHide}
        />
      ))}
    </div>
  );
}

const Wrapper = ({
  item,
  shouldHide,
  onStatesChange,
}: {
  shouldHide?: boolean;
  item: FilterStateProps["groups"][number];
  onStatesChange: (value: string, parentLabel?: string) => void;
}) => {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <div>
      <div className="flex justify-between sticky -top-6 bg-neutral-foreground z-10 py-3">
        <p className="font-semibold text-h6 text-heading">{item.label}</p>
        {shouldHide && (
          <button
            type="button"
            className="cursor-pointer"
            onClick={() => setIsOpen(!isOpen)}
          >
            <Icon type="far" name={isOpen ? "chevron-down" : "chevron-up"} />
          </button>
        )}
      </div>
      <div className={cn("flex flex-col gap-3", isOpen ? "block" : "hidden")}>
        {item.status.map((status, idx) => (
          <State
            key={`${status.label}-${idx}`}
            label={status.label}
            value={status.value}
            children={status.children}
            onStatesChange={onStatesChange}
          />
        ))}
      </div>
    </div>
  );
};

const State = ({
  label,
  value,
  children,
  onStatesChange,
}: {
  label: string;
  value: boolean;
  children: FilterStatus[];
  onStatesChange: (value: string, parentLabel?: string) => void;
}) => {
  return (
    <div className="flex flex-col gap-2 py-4 border-b border-neutral-silent">
      <div className="flex gap-3 items-center">
        <Checkbox
          className="w-5 h-5 focus:ring-1! font-bold cursor-pointer"
          checked={value}
          onChange={() => onStatesChange(label)}
        />
        <p className="text-body-loud font-normal leading-3xl">{label}</p>
      </div>
      <div className="pl-6 flex flex-col gap-3">
        {children.map((item) => (
          <div key={item.label} className="flex gap-3 items-center">
            <Checkbox
              className="w-5 h-5 focus:ring-1! font-bold cursor-pointer"
              checked={item.value}
              onChange={() => onStatesChange(item.label, label)}
            />
            <Badge
              variant={item.icon?.color}
              shape="pill"
              size="default"
              fill="ghost"
              onClick={() => {
                onStatesChange(item.label);
              }}
            >
              <Icon
                type={item.icon?.type || "far"}
                name={item.icon?.name || "question"}
                className={`text-${item.icon?.color || "neutral"}`}
              />
              {item.label}
            </Badge>
          </div>
        ))}
      </div>
    </div>
  );
};
